<template>
  <div class="flex flex-col gap-4" :class="hasGroups && 'my-4'">
    <div v-for="(items, group) in groupedCartItems" :class="hasGroups && 'border rounded-lg mx-2'">
      <div class="text-sm px-2 py-1 rounded-t-lg border-b bg-gray-50 mb-1" v-if="hasGroups">
        Items for
        <span class="font-bold">
          {{
            new Intl.DateTimeFormat('en-US', {
              month: 'long',
              day: 'numeric',
              weekday: 'long'
            }).format(serverDayToDateObject(group))
          }}
        </span>
      </div>
      <component
        :is="animated ? 'TransitionGroup' : 'ul'"
        class="divide-y divide-gray-200 px-6"
        v-bind="animated ? drawerTransitionProps : undefined"
      >
        <li v-for="item in items" :key="`cart-item-${item.attributes.frontend_key || item.id}`">
          <div
            v-if="(!isItemAvailable(item) || item.attributes.order_summary_error_badge_text) && !bottleIsPaid"
            class="pt-3 flex flex-wrap gap-1"
          >
            <span
              v-if="!isItemAvailable(item)"
              class="bg-yellow-100 border border-yellow-700 text-yellow-700 text-xxs font-manrope uppercase px-1.5 py-0.5 rounded-lg tracking-widest"
            >
              Unavailable
            </span>
            <span
              v-if="item.attributes.order_summary_error_badge_text"
              class="bg-yellow-100 border border-yellow-700 text-yellow-700 text-xxs font-manrope uppercase px-1.5 py-0.5 rounded-lg tracking-widest"
            >
              {{ item.attributes.order_summary_error_badge_text }}
            </span>
          </div>
          <OrderSummaryItem
            :image-url="itemImageUrl(item)"
            :name="productAttributes(item)?.product_name || ''"
            :variant-text="variantText(item)"
            :price-formatted="showPrice(item) ? item.attributes.total_price.format : ''"
            :original-price-formatted="showPrice(item) ? originalPriceFormatted(item) : null"
            :quantity="item.attributes.quantity"
            :included-quantity="item.attributes.included_quantity || 0"
            :editable="!readOnly && !bottleIsPaid && isItemAvailable(item)"
            :read-only="readOnly || bottleIsPaid"
            :loading="item.attributes.frontend_key === updatingKey"
            v-bind="quantityRulesFor(item)"
            :remove-reason="removeReasonFor(item)"
            :allow-edit="canEditItem(item)"
            @update-quantity="onUpdateQuantity(item, $event)"
            @remove="onRemove(item)"
            @edit="$emit('edit-line', item)"
          />
          <div v-if="enableGiftCardView && isGiftCardItem(item)" class="pb-3 -mt-1 pl-[60px] text-left">
            <a class="font-light text-xs underline cursor-pointer" @click.stop="$emit('view-gift-cards')">
              Resend Gift Card
            </a>
          </div>
        </li>
        <!-- <li>for an extra divider</li> -->
      </component>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { TransitionGroup } from 'vue'
import { serverDayToDateObject } from '@/helpers/dates'
import { quantityCap } from '@/models/productViewModel'
import { trackCartLineQuantityChange } from '@/utils/cartLineAnalytics'
import OrderSummaryItem from '@/components/Checkout/OrderSummaryItem'

export default {
  components: { OrderSummaryItem, TransitionGroup },
  props: {
    bottle: {
      type: Object
    },
    cartItems: {
      type: Array
    },
    align: {
      type: String,
      default: 'top',
      validator: (value) => ['top', 'center'].includes(value)
    },
    enableGiftCardView: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    newestFirst: {
      type: Boolean,
      default: false
    },
    animated: {
      type: Boolean,
      default: false
    },
    enforceQuantityRules: {
      type: Boolean,
      default: false
    },
    allowSoldOutRemoval: {
      type: Boolean,
      default: false
    },
    trackQuantityChanges: {
      type: Boolean,
      default: false
    },
    allowEdit: {
      type: Boolean,
      default: false
    }
  },
  emits: ['view-gift-cards', 'edit-line'],
  data() {
    return { updatingKey: null }
  },
  computed: {
    cartSubmitting() {
      return this.$store.state.activelySubmittingCart
    },
    // Drawer-only: without `animated` the list renders the same plain <ul> it
    // always has, so non-drawer surfaces (checkout, receipts) are untouched.
    drawerTransitionProps() {
      return {
        tag: 'ul',
        enterActiveClass: 'transition-all duration-300 ease-out',
        enterFromClass: 'opacity-0 -translate-y-3',
        enterToClass: 'opacity-100 translate-y-0',
        moveClass: 'transition-transform duration-300'
      }
    },
    groupedCartItems() {
      const slotDay = this.$store.getters.fulfillmentSlot?.attributes.fulfillment_date
      this.cartItems.map((x) => (x.attributes.fulfillment_group_date = x.attributes.fulfillment_group_date ?? slotDay))
      let items = _.sortBy(this.cartItems, 'attributes.fulfillment_group_date')
      const grouped = _.groupBy(items, 'attributes.fulfillment_group_date')
      if (this.newestFirst) {
        Object.keys(grouped).forEach((key) => grouped[key].reverse())
      }
      return grouped
    },
    unavailableItems() {
      return this.bottle?.relationships?.removed_category_product_store_pairs_still_in_cart?.data
    },
    bottleIsPaid() {
      return this.bottle?.attributes?.payment_status === 'paid'
    },
    linesChangeable() {
      return !this.readOnly && !this.bottleIsPaid
    },
    hasGroups() {
      return Object.keys(this.groupedCartItems).length > 1
    }
  },
  watch: {
    cartSubmitting(val) {
      if (!val) this.updatingKey = null
    }
  },
  methods: {
    quantity(item) {
      return item.attributes.quantity
    },
    isItemAvailable(item) {
      // Check if the unavailableItems array contains the given item
      const unavailableItem = this.unavailableItems?.find(
        (unavailable) => unavailable.id.toString() === item.attributes.category_product_store_pair_id.toString()
      )
      // If the item is not found in the unavailableItems array, it is available
      return !unavailableItem
    },
    productAttributes(item) {
      return this.$store.state.objects.product?.[item?.relationships?.product?.data?.id]?.attributes
    },
    categoryProductStorePair(item) {
      return this.$store.state.objects.category_product_store_pair?.[item?.attributes?.category_product_store_pair_id]
    },
    quantityRulesFor(item) {
      if (!this.enforceQuantityRules) return {}
      const product = this.productAttributes(item) || {}
      return {
        step: Number(product.step) || 1,
        floor: Number(product.starting_quantity) || 0,
        max: quantityCap({
          inventoryRemaining: this.categoryProductStorePair(item)?.attributes?.quantity_remaining,
          merchantMax: product.max_per_order,
          cartQuantity: item.attributes.quantity
        })
      }
    },
    removeReasonFor(item) {
      if (!this.allowSoldOutRemoval || !this.linesChangeable || this.isItemAvailable(item)) return null
      return 'unavailable'
    },
    canEditItem(item) {
      return (
        this.allowEdit &&
        this.linesChangeable &&
        this.isItemAvailable(item) &&
        !this.isGiftCardItem(item) &&
        !!this.productAttributes(item) &&
        !!this.categoryProductStorePair(item)
      )
    },
    trackQuantityChange(item, quantity) {
      if (!this.trackQuantityChanges) return
      const product = this.productAttributes(item)
      trackCartLineQuantityChange(this.$store, {
        from: item.attributes.quantity,
        to: quantity,
        isUpsell: item.attributes.type === 'UpsellCartItem',
        productId: product?.id ?? item.relationships?.product?.data?.id,
        productName: product?.product_name
      })
    },
    isGiftCardItem(item) {
      return this.productAttributes(item)?.type === 'GiftCardProduct'
    },
    variantDescription(variantOptionSelection, i, count) {
      return variantOptionSelection?.variant_option_name + this.variantSecondLevelDescription(variantOptionSelection)
    },
    variantSecondLevelDescription(variantOptionSelection) {
      return variantOptionSelection?.variant_option_description
        ? ' (' + variantOptionSelection?.variant_option_description + ')'
        : ''
    },
    showPrice(item) {
      const cpsp = this.categoryProductStorePair(item)
      if (cpsp?.attributes) {
        return cpsp.attributes.show_prices || item.attributes.type === 'CustomCartItem'
      }
      // V2's Selection page skips the `category_product_store_pairs` getBottle
      // include (the V2 store snapshot owns that data), so on Login/Checkout —
      // which still render the V1 OrderSummarySection + CartItems via the
      // `hasV2CartItems` false branch — the CPSP lookup misses for every row.
      // Fall back to the bottle's `show_prices` so prices render in both flows
      // (defaults to true; only an explicit `show_prices: false` hides them).
      return this.bottle?.attributes?.show_prices !== false || item.attributes.type === 'CustomCartItem'
    },
    goToCart() {
      this.$router.push({ name: 'Selection', params: { bottleid: this.bottle?.id }, query: { showCart: 'y' } })
    },
    itemImageUrl(item) {
      const productId = item?.relationships?.product?.data?.id
      const product = productId && this.$store.state.objects.product?.[productId]
      if (!product?.relationships?.attachments?.data?.length) return null
      const attachments = this.$store.getters.attachments(productId) || []
      return attachments[0]?.attributes?.secure_url || null
    },
    variantText(item) {
      const selections = item.attributes.variant_options_selection || []
      return (
        selections
          .map((sel, i) => {
            const base = this.variantDescription(sel, i, selections.length)
            const qty = (sel.quantity || 0) > 1 ? ` x ${sel.quantity}` : ''
            return `${base}${qty}`
          })
          .join(', ') || null
      )
    },
    originalPriceFormatted(item) {
      const def = item.attributes.default_price_plus_variants_price
      const totalPrice = item.attributes.total_price
      if (!def || !totalPrice || totalPrice.cents == null) return null
      const originalLineCents = def.cents * (item.attributes.quantity || 1)
      if (originalLineCents <= totalPrice.cents) return null
      const currency = (def.format || '$').replace(/[\d.,\s]/g, '')[0] || '$'
      return `${currency}${(originalLineCents / 100).toFixed(2)}`
    },
    submittableForItem(item) {
      const key = item.attributes.frontend_key
      return this.$store.getters.submittableCart?.find((s) => s.frontend_key === key)
    },
    onUpdateQuantity(item, quantity) {
      this.updatingKey = item.attributes.frontend_key
      const submittable = this.submittableForItem(item)
      if (!submittable) return
      this.trackQuantityChange(item, quantity)
      this.$store.dispatch('updateSubmittableCart', {
        submittableCartItem: Object.assign({}, submittable, { quantity }),
        forceCheckout: true
      })
    },
    onRemove(item) {
      this.onUpdateQuantity(item, 0)
    },
    serverDayToDateObject
  }
}
</script>
